import { fitBounds } from 'google-map-react/utils'
import first from 'lodash/first'
import minBy from 'lodash/minBy'
import maxBy from 'lodash/maxBy'
import remove from 'lodash/remove'

const minValue = (arr, prop) => minBy(arr, prop)[prop]
const maxValue = (arr, prop) => maxBy(arr, prop)[prop]

// Add this value to lats/longs to account for map marker size
const BOUNDS_BUFFER = 0.0004

// Geometry helpers: robust point-in-polygon with boundary-inclusive semantics
function isPointOnLineSegment(px, py, x1, y1, x2, y2) {
  // Check colinearity via cross product and bounding box inclusion
  const cross = (py - y1) * (x2 - x1) - (px - x1) * (y2 - y1)
  if (Math.abs(cross) > 1e-12) return false
  const dot = (px - x1) * (px - x2) + (py - y1) * (py - y2)
  return dot <= 1e-12
}

function isPointInRing(px, py, ring) {
  // ring: array of [lng, lat]
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0]
    const yi = ring[i][1]
    const xj = ring[j][0]
    const yj = ring[j][1]
    // Boundary check first
    if (isPointOnLineSegment(px, py, xj, yj, xi, yi)) return true
    const intersect =
      yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

function pointInPolygon(lng, lat, geometry) {
  if (!geometry) return true
  const type = geometry.type
  if (type === 'Polygon') {
    const [outer, ...holes] = geometry.coordinates
    if (!outer || !outer.length) return false
    if (!isPointInRing(lng, lat, outer)) return false
    for (let k = 0; k < holes.length; k += 1) {
      if (isPointInRing(lng, lat, holes[k])) return false
    }
    return true
  }
  if (type === 'MultiPolygon') {
    const polys = geometry.coordinates
    for (let p = 0; p < polys.length; p += 1) {
      const [outer, ...holes] = polys[p]
      if (!outer || !outer.length) continue
      if (isPointInRing(lng, lat, outer)) {
        let inHole = false
        for (let k = 0; k < holes.length; k += 1) {
          if (isPointInRing(lng, lat, holes[k])) {
            inHole = true
            break
          }
        }
        if (!inHole) return true
      }
    }
    return false
  }
  if (type === 'Feature') return pointInPolygon(lng, lat, geometry.geometry)
  if (type === 'FeatureCollection') {
    const f = Array.isArray(geometry.features) && geometry.features[0]
    return pointInPolygon(lng, lat, f ? f.geometry : null)
  }
  return true
}

// Get bounds containing locations
function getBoundsForLocations(locations) {
  const minLng = minValue(locations, 'longitude') - BOUNDS_BUFFER
  const maxLng = maxValue(locations, 'longitude') + BOUNDS_BUFFER
  const minLat = minValue(locations, 'latitude') - BOUNDS_BUFFER
  const maxLat = maxValue(locations, 'latitude') + BOUNDS_BUFFER
  return {
    nw: { lat: maxLat, lng: minLng },
    se: { lat: minLat, lng: maxLng },
  }
}

// Get center and zoom for single location
function fitToLocation(location) {
  return {
    zoom: 10,
    center: { lat: location.latitude, lng: location.longitude },
  }
}

// returns true if location is within the configured polygon boundary
// Points exactly on the boundary are considered inside
function isWithinBounds(location, boundary) {
  // If boundary not yet loaded, treat as inside to avoid dropping markers
  if (!boundary) return true
  const geom =
    boundary.type === 'Feature'
      ? boundary.geometry
      : boundary.geometry || boundary
  const withinBounds = pointInPolygon(
    location.longitude,
    location.latitude,
    geom
  )
  if (!withinBounds)
    console.warn(`Location ${location.name} is not within bounds`) // eslint-disable-line
  return withinBounds
}

// Get center and zoom props to scale map to show given locations
export function fitToLocations(locations, size, boundary) {
  // use only locations within polygon bounds
  const locationsWithinBounds = remove([...locations], (location) =>
    isWithinBounds(location, boundary)
  )
  if (locationsWithinBounds.length === 1) {
    return fitToLocation(first(locationsWithinBounds))
  }
  if (!locationsWithinBounds.length) {
    return {}
  }
  const bounds = getBoundsForLocations(locationsWithinBounds)
  const { center, zoom } = fitBounds(bounds, size)
  return { center, zoom }
}

export function findCenter(selectedLocation, currentCenter) {
  return selectedLocation
    ? { lat: selectedLocation.latitude, lng: selectedLocation.longitude }
    : currentCenter
}

// Project a lat/lng to absolute Web Mercator pixel coordinates at a given zoom.
const TILE_SIZE = 256
function project(latitude, longitude, worldSize) {
  const sin = Math.sin((latitude * Math.PI) / 180)
  const x = ((longitude + 180) / 360) * worldSize
  const clampedSin = Math.min(Math.max(sin, -0.9999), 0.9999)
  const y =
    (0.5 - Math.log((1 + clampedSin) / (1 - clampedSin)) / (4 * Math.PI)) *
    worldSize
  return { x, y }
}

// Grid-based clustering: groups locations that fall within the same ~cellSize
// pixel cell at the current zoom. Zooming in expands the world (in pixels), so
// points spread into separate cells and clusters break apart into individual
// pins. Returns clusters with a representative (centroid) position and count.
export function getClusters(locations, zoom, cellSize = 70) {
  const worldSize = TILE_SIZE * Math.pow(2, zoom)
  const cells = new Map()

  locations.forEach((location) => {
    if (location.latitude == null || location.longitude == null) return
    const { x, y } = project(location.latitude, location.longitude, worldSize)
    const key = `${Math.floor(x / cellSize)}_${Math.floor(y / cellSize)}`
    if (!cells.has(key)) cells.set(key, [])
    cells.get(key).push(location)
  })

  return Array.from(cells.values()).map((points) => {
    const count = points.length
    const latitude = points.reduce((sum, p) => sum + p.latitude, 0) / count
    const longitude = points.reduce((sum, p) => sum + p.longitude, 0) / count
    return {
      id: `${points[0].resourceId || points[0].name}-${count}`,
      latitude,
      longitude,
      count,
      points,
    }
  })
}
